import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {UseFormReturn} from 'react-hook-form';
import ReactHookFormSelect from '../../../components/reactHookFormComponents/ReactHookFormSelect';
import {presenterOptions} from '../utils/mappers';
import {ANONYMOUS_PRESENTER_VALUE} from '../../../../incase-domain/validators/room/room';
import ReactHookFormInputFieldWithCounter from '../../../components/reactHookFormComponents/ReactHookFormInputFieldWithCounter';
import {ROOM_ANONYMOUS_PRESENTER_EMAIL_MAX_LENGTH} from '../../../../incase-domain/common/validation';
import {useFetchPresenters} from '../queries';
import {FILTER_USER_STATE, Role} from '../../../../incase-domain/user';
import {SORT_DEFAULT} from '../../../../constants';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';

interface IPresentersProps {
    presenterId: string;
    presenterSearchTerm: string;
    setPresenterSearchTerm: (searchTerm: string) => void;
    useForm: UseFormReturn;
}

function SelectPresenter({presenterId, presenterSearchTerm, useForm, setPresenterSearchTerm}: IPresentersProps): JSX.Element {
    const {t} = useTranslation();
    const user = useSelector(loggedInUserSelector);
    const {control, formState} = useForm;
    const {errors} = formState;

    const DEFAULT_PRESENTER_OPTIONS = useMemo(
        () => [
            {label: `Me (${user?.email})` as string, value: user?._id as string},
            {label: 'Anonymous', value: ANONYMOUS_PRESENTER_VALUE},
        ],
        [user]
    );
    const {
        data: presenterData,
        fetchNextPage,
        hasNextPage,
    } = useFetchPresenters({
        searchTerm: presenterSearchTerm,
        filterQuery: {
            archived: FILTER_USER_STATE.ACTIVE,
            roles: [Role.SUPERUSER, Role.ADMIN, Role.PRESENTER, Role.CREATOR],
        },
        sortBy: SORT_DEFAULT,
    });

    const handleScrollToBottom = (): void => {
        if (hasNextPage) fetchNextPage();
    };

    return (
        <>
            <ReactHookFormSelect
                fieldName="presenterId"
                options={[...DEFAULT_PRESENTER_OPTIONS, ...presenterOptions(presenterData)]}
                useForm={useForm}
                placeholder={`${t('roomsPage.selectPresenter')}`}
                setSearchTerm={setPresenterSearchTerm}
                dataCy="presenter_select"
                onScrollToBottom={handleScrollToBottom}
            />
            {presenterId === ANONYMOUS_PRESENTER_VALUE && (
                <div className="u-mt-4" data-cy="presenter_email_input">
                    <ReactHookFormInputFieldWithCounter
                        text={`${t('roomsPage.createRoomModal.presenterEmail')}`}
                        fieldName="presenterEmail"
                        errors={errors}
                        control={control}
                        maxLength={ROOM_ANONYMOUS_PRESENTER_EMAIL_MAX_LENGTH}
                        className="c-sidepanel__input"
                        textClassName="c-room_modal__input__text"
                    />
                </div>
            )}
        </>
    );
}

export default SelectPresenter;
